import { useNavigate, useSearch } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { DeltaCell } from "@/components/cinch/DeltaCell";
import { KpiTile } from "@/components/cinch/KpiTile";
import { Matrix, type MatrixCell } from "@/components/cinch/Matrix";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";

import type { PostHogSearch } from "..";
import { useOverview, useRefreshOverview } from "../api";
import { buildFeatureRows, formatWindow, heatTone, type FeatureRow } from "../lib";
import { FeatureDrawer } from "./FeatureDrawer";
import { SetupNotice } from "./SetupNotice";
import { TopUsers } from "./TopUsers";
import { UsageTimeline } from "./UsageTimeline";

/** How many features get a column in the who-uses-what heatmap. */
const MATRIX_FEATURE_LIMIT = 12;

function buildColumns(
  onOpenExpanded: (featureKey: string) => void,
): DataTableColumn<FeatureRow>[] {
  return [
  {
    key: "label",
    header: "Feature",
    sortable: true,
    // The name is its own control: row-click opens the quick view, clicking
    // the name goes straight to the full view. stopPropagation keeps the row
    // handler from also firing and collapsing it back to quick.
    render: (r) => (
      <span className="flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenExpanded(r.feature_key);
          }}
          className="truncate font-medium underline-offset-2 hover:text-power hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title="Open the full view"
        >
          {r.label ?? r.feature_key}
        </button>
        {!r.inRegistry ? (
          <StatusBadge
            value="archived"
            label="Retired"
            className="shrink-0"
          />
        ) : null}
      </span>
    ),
  },
  {
    key: "nav_group",
    header: "Group",
    sortable: true,
    render: (r) => <span className="text-muted-foreground">{r.nav_group ?? "—"}</span>,
  },
  {
    key: "events",
    header: "Opens",
    align: "right",
    numeric: true,
    sortable: true,
    render: (r) => <span className="tabular-nums">{r.events.toLocaleString()}</span>,
  },
  {
    key: "users",
    header: "People",
    align: "right",
    numeric: true,
    sortable: true,
    render: (r) => <span className="tabular-nums">{r.users}</span>,
  },
  {
    key: "delta_events",
    header: "vs prior",
    align: "right",
    numeric: true,
    sortable: true,
    // Null (rather than 0) when both periods are empty, so a never-used
    // feature shows "—" instead of a misleading "no change".
    // DeltaCell formats as currency by default — these are event counts, so
    // it needs an explicit integer formatter or they render as "▲ $5".
    render: (r) => (
      <DeltaCell
        value={r.prior_events === 0 && r.events === 0 ? null : r.delta_events}
        format={(n) => Math.abs(n).toLocaleString()}
      />
    ),
  },
  {
    key: "exceptions",
    header: "Errors",
    align: "right",
    numeric: true,
    sortable: true,
    render: (r) =>
      r.exceptions > 0 ? (
        <span className="tabular-nums text-danger">{r.exceptions}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  ];
}

export function OverviewTab({ days }: { days: number }) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/system/posthog" }) as PostHogSearch;
  const { data, isLoading, error } = useOverview(days);
  const refresh = useRefreshOverview();
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);

  const go = (next: Partial<PostHogSearch>) =>
    navigate({
      to: "/system/posthog",
      search: (prev: PostHogSearch) => ({ ...prev, ...next }),
    });

  const featureColumns = useMemo(
    () => buildColumns((featureKey) => go({ feature: featureKey, expanded: true })),
    // `go` closes over navigate only, which is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const unsorted = useMemo(() => buildFeatureRows(data?.features ?? []), [data]);
  const selectedRow = search.feature
    ? (unsorted.find((r) => r.feature_key === search.feature) ?? null)
    : null;

  const rows = useMemo(() => {
    if (!sort) return unsorted;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...unsorted].sort((a, b) => {
      switch (sort.key) {
        case "events":
          return (a.events - b.events) * dir;
        case "users":
          return (a.users - b.users) * dir;
        case "delta_events":
          return (a.delta_events - b.delta_events) * dir;
        case "exceptions":
          return (a.exceptions - b.exceptions) * dir;
        case "nav_group":
          return (a.nav_group ?? "").localeCompare(b.nav_group ?? "") * dir;
        default:
          return (a.label ?? a.feature_key).localeCompare(b.label ?? b.feature_key) * dir;
      }
    });
  }, [unsorted, sort]);
  const neverOpened = rows.filter((r) => r.neverOpened && r.inRegistry);
  const used = rows.filter((r) => r.events > 0);

  // Columns are the busiest features — a grid of every feature would be mostly
  // empty cells and unreadable at this width.
  const matrixFeatures = used.slice(0, MATRIX_FEATURE_LIMIT);
  const users = data?.users ?? [];
  const maxCell = Math.max(
    1,
    ...users.flatMap((u) => matrixFeatures.map((f) => u.by_feature[f.feature_key] ?? 0)),
  );

  const cells: MatrixCell[][] = users.map((u) =>
    matrixFeatures.map((f) => {
      const n = u.by_feature[f.feature_key] ?? 0;
      return {
        value: n || "",
        tone: heatTone(n, maxCell),
        title: `${u.name ?? u.email ?? "Unknown"} — ${f.label ?? f.feature_key}: ${n} opens`,
      };
    }),
  );

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading usage…</div>;
  }

  if (error) {
    // 409 = not configured yet, which is a setup prompt rather than a failure.
    if (error instanceof ApiError && error.status === 409) {
      return <SetupNotice detail={error.message} />;
    }
    return (
      <div className="rounded-lg border border-danger/40 bg-danger/5 p-4 text-sm">
        <div className="font-semibold text-danger">Couldn&apos;t load usage</div>
        <p className="mt-1 text-muted-foreground">
          {error instanceof Error ? error.message : String(error)}
        </p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{formatWindow(data.period_start, data.period_end)}</span>
          {data.stale ? (
            <StatusBadge
              value="flagged"
              label="Stale — PostHog unreachable, showing the last good pull"
            />
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refresh.mutate(days)}
          disabled={refresh.isPending}
        >
          <RefreshCw
            className={`mr-2 h-3.5 w-3.5 ${refresh.isPending ? "animate-spin" : ""}`}
          />
          {refresh.isPending ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile label="Active people" value={data.active_users} />
        <KpiTile label="Feature opens" value={data.total_events.toLocaleString()} />
        <KpiTile
          label="Features used"
          value={used.length}
          sub={`of ${rows.filter((r) => r.inRegistry).length} built`}
        />
        <KpiTile
          label="Never opened"
          value={neverOpened.length}
          tone={neverOpened.length > 0 ? "warning" : "positive"}
          sub="in this time window"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <UsageTimeline daily={data.daily} />
        </div>
        <TopUsers users={data.users} limit={3} />
      </div>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          By feature
        </h2>
        <p className="text-xs text-muted-foreground">
          Click a row for the quick view; click the feature name for the full view.
        </p>
        <DataTable
          columns={featureColumns}
          rows={rows}
          rowKey={(r) => r.feature_key}
          sort={sort}
          onSortChange={setSort}
          emptyMessage="No feature activity in this window."
          onRowClick={(r) => go({ feature: r.feature_key, expanded: undefined })}
          selectedKey={search.feature ?? null}
        />
      </section>

      <FeatureDrawer
        featureKey={search.feature ?? null}
        label={selectedRow?.label ?? search.feature ?? null}
        navGroup={selectedRow?.nav_group ?? null}
        days={days}
        expanded={search.expanded === true}
        onOpenChange={(v) => {
          if (!v && !search.expanded) go({ feature: undefined, expanded: undefined });
        }}
        onExpandedChange={(exp) => go({ expanded: exp ? true : undefined })}
        onClose={() => go({ feature: undefined, expanded: undefined })}
      />

      {users.length > 0 && matrixFeatures.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Who uses what
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border p-3">
            <Matrix
              cornerLabel="Person"
              columns={matrixFeatures.map((f) => (
                <span key={f.feature_key} className="block max-w-[7rem] truncate">
                  {f.label ?? f.feature_key}
                </span>
              ))}
              rows={users.map((u) => (
                <span key={u.user_id} className="block max-w-[10rem] truncate">
                  {u.name ?? u.email ?? "Unknown"}
                </span>
              ))}
              cells={cells}
            />
          </div>
        </section>
      ) : null}

      {neverOpened.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Never opened in this window
          </h2>
          <p className="text-sm text-muted-foreground">
            Built and shipped, but nobody went there. Worth asking whether it needs
            promoting, fixing, or retiring — set a goal on the Goals tab so the weekly
            review can judge it against what it was for.
          </p>
          <div className="flex flex-wrap gap-2">
            {neverOpened.map((r) => (
              <StatusBadge
                key={r.feature_key}
                value="inactive"
                label={`${r.label ?? r.feature_key}${r.nav_group ? ` · ${r.nav_group}` : ""}`}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
